import * as React from 'react';
import {
	Alert,
	Button,
	Dropdown,
	Icon,
	Spinner,
	Typography,
} from '@/components/common';
import { TooltipComponent } from '@/components/common/tooltip';
import Icons from '@/const/icons';
import { useNotification } from '@/hooks';
import useModal from '@/hooks/useModal';
import { CasesByAdminLawyer, Option, TeamMember } from '@/interfaces';
import { userLawyerService } from '@/api';
import { TriangleAlert } from 'lucide-react';
import { InputEmail, InputText } from '@/components/form';
import { useForm } from 'react-hook-form';
import { CasesListComponent } from './CasesList';

const rules = {
	email: {
		required: { value: true, message: 'This is required.' },
	},
};

interface ActionColumnProps {
	user: TeamMember;
	refetch: () => void;
}

export const ActionColumn: React.FC<ActionColumnProps> = ({
	user,
	refetch,
}) => {
	const { toastError, toastSuccess } = useNotification();
	const {
		register,
		reset,
		getValues,
		setValue,
		formState: { isValid },
		watch,
	} = useForm({ mode: 'onChange' });
	const [actionsOptions, setActionsOptions] = React.useState<Option[]>([]);
	const [casesList, setCasesList] = React.useState<CasesByAdminLawyer[]>([]);
	const [isLoading, setIsLoading] = React.useState<boolean>(false);

	const {
		Modal: ModalDeactivateAccount,
		hide: hideDeactivateAccount,
		show: showDeactivateAccount,
	} = useModal();

	const {
		Modal: ModalDeleteAccount,
		hide: hideDeleteAccount,
		show: showDeleteAccount,
	} = useModal();

	const {
		Modal: ModalReactivateAccount,
		hide: hideReactivateAccount,
		show: showReactivateAccount,
	} = useModal();

	const {
		Modal: ModalChangeEmail,
		hide: hideChangeEmail,
		show: showChangeEmail,
	} = useModal();

	React.useEffect(() => {
		const auxOptions: Option[] = [];

		if (!user.deleted_at) {
			auxOptions.push({
				icon: Icons.at,
				label: 'Change email',
				name: 'Change email',
				onClick: () => showChangeEmail(),
			});
		}

		if (user.deleted_at) {
			auxOptions.push({
				icon: Icons.profileTick,
				label: 'Reactivate account',
				name: 'Reactivate account',
				onClick: () => showReactivateAccount(),
			});
		} else if (user.last_active) {
			auxOptions.push({
				icon: Icons.profileDelete,
				label: 'Deactivate account',
				name: 'Deactivate account',
				onClick: () => startDeactivateAccount(),
			});
		}

		auxOptions.push({
			icon: Icons.trash,
			label: 'Delete Account',
			name: 'Delete Account',
			onClick: () => startDeleteAccount(),
			classNameItemIcon: '!text-main-red',
			classNameItemLabel: 'text-main-red',
		});

		setActionsOptions(auxOptions);
	}, [user]);

	const startDeactivateAccount = async (): Promise<void> => {
		setIsLoading(true);
		setCasesList([]); // reset cases list
		try {
			const res = await userLawyerService.asyncListLawyerCases(user.id);
			setCasesList(res.data);
			showDeactivateAccount();
		} catch (error) {
			console.log('Error fetching cases list');
		} finally {
			setIsLoading(false);
		}
	};

	const startDeleteAccount = async (): Promise<void> => {
		setIsLoading(true);
		setCasesList([]); // reset cases list
		try {
			const res = await userLawyerService.asyncListLawyerCases(user.id);
			setCasesList(res.data);
			showDeleteAccount();
		} catch (error) {
			console.log('Error fetching cases list');
		} finally {
			setIsLoading(false);
		}
	};

	const handleResendInvitation = async (): Promise<void> => {
		try {
			await userLawyerService.postResendInvitationLawyer(user.id);
			refetch();
			toastSuccess('Invitation successfully resent');
		} catch (error) {
			toastError('An error has occurred. Please try again later.');
		}
	};

	const handleDeactivateAccount = async (): Promise<void> => {
		try {
			await userLawyerService.deactivateLawyerAccount(user.id);
			refetch();
			toastSuccess('The user has been successfully suspended');
		} catch (error) {
			toastError('An error has occurred. Please try again later.');
		} finally {
			hideDeactivateAccount();
		}
	};

	const handleDeleteAccount = async (): Promise<void> => {
		try {
			await userLawyerService.deleteLawyerAccount(user.id);
			refetch();
			toastSuccess('The lawyer has been successfully removed');
		} catch (error) {
			toastError('An error has occurred. Please try again later.');
		} finally {
			hideDeleteAccount();
		}
	};

	const handleReactivateAccount = async (): Promise<void> => {
		try {
			await userLawyerService.reactivateLawyerAccount(user.id);
			refetch();
			toastSuccess('This account has been successfully activated');
		} catch (error) {
			toastError('An error has occurred. Please try again later.');
		} finally {
			hideReactivateAccount();
		}
	};

	const handleChangEmail = async (): Promise<void> => {
		try {
			const email = getValues('email');
			await userLawyerService.updateLawyerEmail(user.id, email);
			refetch();
			toastSuccess('The email has been changed successfully.');
		} catch (error) {
			toastError('An error has occurred. Please try again later.');
		} finally {
			reset({});
			hideChangeEmail();
		}
	};

	const listCases = casesList ?? [];

	return (
		<>
			<div
				className="flex items-center gap-x-2 justify-end"
				onClick={(event) => {
					event.stopPropagation();
				}}
			>
				{!user.last_active && (
					<TooltipComponent
						label="Resend invitation"
						classNameLabel="font-semibold"
						trigger={
							<button type="button" onClick={handleResendInvitation}>
								<Icon
									src={Icons.sendTwo}
									className="text-black"
									width={24}
									height={24}
								/>
							</button>
						}
					></TooltipComponent>
				)}
				<Dropdown
					buttonContent={<Icon src={Icons.more} className="!size-6" />}
					showChevronDownIcon={false}
					items={actionsOptions}
				/>
			</div>
			<ModalDeactivateAccount
				title="Deactivate account"
				description="Review consequences before proceeding"
				classNameCardContainer="min-w-[605px]"
				classNameTitle="text-left"
				withXButton
			>
				{isLoading ? (
					<div className="flex justify-center my-20">
						<Spinner />
					</div>
				) : (
					<>
						<Alert
							type="error"
							icon={<TriangleAlert className="text-[#FF0043] !size-5" />}
							text={
								<div>
									<Typography
										type={'regular-16'}
										className={'text-main-red text-left'}
									>
										Deactivation
									</Typography>
									<Typography
										type={'regular-14'}
										className={'text-gray-80 mt-2'}
									>
										{`You are about to deactivate “${user.firstname} ${user.lastname}”`}
									</Typography>
								</div>
							}
							className="rounded-none !p-3 !items-start !gap-2"
						/>
						{listCases.length > 0 && (
							<>
								<Typography type="regular-16" className="my-6 text-left">
									The following <b>{listCases.length} cases</b> will be
									reassigned to the Admin
								</Typography>
								<div className="h-[240px] overflow-y-auto overflow-x-hidden scroll-custom">
									<CasesListComponent
										cases={listCases}
										classNameCard="h-[88px]"
									/>
								</div>
							</>
						)}
					</>
				)}

				<div className="flex items-center w-full justify-end gap-6 mt-6">
					<Button
						decoration="line-primary"
						size="fit"
						label="Cancel"
						classNameLabel="!font-semibold !text-base"
						onClick={hideDeactivateAccount}
					/>
					<Button
						decoration="danger-outline"
						size="fit"
						label="Deactivate Account"
						className="!bg-transparent"
						classNameLabel="!font-semibold !text-base"
						onClick={handleDeactivateAccount}
					/>
				</div>
			</ModalDeactivateAccount>
			<ModalDeleteAccount
				title="Delete Lawyer"
				description="Review consequences before proceeding"
				classNameCardContainer="!w-[720px]"
				classNameTitle="text-left"
				withXButton
			>
				{isLoading ? (
					<div className="flex justify-center my-20">
						<Spinner />
					</div>
				) : (
					<>
						<Alert
							type="error"
							icon={<TriangleAlert className="text-[#FF0043] !size-6" />}
							text={
								<div>
									<Typography
										type={'regular-16'}
										className={'text-main-red text-left'}
									>
										Permanent Deletion
									</Typography>
									<Typography
										type={'regular-14'}
										className={'text-gray-80 mt-2 text-left'}
									>
										{`You are about to permanently delete “${user.firstname} ${user.lastname}”. This
										action is irreversible and will also delete all associated
										data.`}
									</Typography>
								</div>
							}
							className="rounded-none !p-3 !items-start !gap-2 !bg-[#FF00431A]"
						/>
						{listCases.length > 0 && (
							<>
								<Typography type="regular-16" className="my-6 text-left">
									The following <b>{listCases.length} cases</b> will be
									reassigned to the Admin
								</Typography>
								<div className="h-[240px] overflow-y-auto overflow-x-hidden scroll-custom">
									<CasesListComponent
										cases={listCases}
										classNameCard="h-[88px]"
									/>
								</div>
							</>
						)}
					</>
				)}
				<div className="mt-6">
					<Typography type="regular-16" className="text-left">
						To confirm this action, please type{' '}
						<span className="text-[#FF0043] font-semibold">DELETE</span> into
						the box below.
					</Typography>
					<InputText
						name="delete"
						register={register}
						placeholder="DELETE"
						classNameInput="!bg-transparent"
					/>
				</div>
				<div className="flex items-center w-full justify-end gap-6 mt-6">
					<Button
						decoration="line-primary"
						size="fit"
						label="Cancel"
						classNameLabel="!font-semibold !text-base"
						onClick={hideDeleteAccount}
					/>
					<Button
						decoration="danger-outline"
						size="fit"
						label="Delete Lawyer"
						className="!bg-transparent"
						classNameLabel="!font-semibold !text-base"
						disabled={watch('delete') !== 'DELETE'}
						onClick={handleDeleteAccount}
					/>
				</div>
			</ModalDeleteAccount>
			<ModalReactivateAccount classNameCardContainer="min-w-[605px] !py-16 !px-12">
				<Typography type="custom-h3" className="text-h3 font-bold h-[50px]">
					Reactivate account
				</Typography>
				<Typography
					type="regular-18"
					className="text-center mt-4 mb-12 w-full text-[#0D0616] h-[27px]"
				>
					{`Are you sure you want to reactivate this account?`}
				</Typography>
				<div className="flex items-center w-full justify-center gap-[15px]">
					<Button
						decoration="line-primary"
						size="full"
						label="Cancel"
						className="!h-16"
						classNameLabel="!font-semibold !text-base"
						onClick={hideReactivateAccount}
					/>
					<Button
						decoration="primary"
						size="full"
						label="Yes, Reactivate"
						className="!h-16"
						classNameLabel="!font-semibold !text-base"
						onClick={handleReactivateAccount}
					/>
				</div>
			</ModalReactivateAccount>
			<ModalChangeEmail classNameCardContainer="!w-[605px] !py-16 !px-12">
				<Typography type="custom-h3" className="text-h3 font-bold h-[50px]">
					Change email
				</Typography>
				<Typography
					type="regular-18"
					className="text-center mt-4 mb-12 w-full text-[#0D0616]"
				>
					{`An email will be sent to the lawyer at the new email address provided, where they will need to log in.`}
				</Typography>
				<InputEmail
					disabled
					name="current-email"
					title="Current Email"
					register={register}
					defaultValue={user.email}
					classNameInput="group-hover:disabled:bg-[#0D061633] !bg-[#0D061633] disabled:!text-[#0D0616] opacity-[65%]"
				/>
				<InputEmail
					name="email"
					title="New Email"
					register={register}
					rules={rules.email}
					className="mt-4"
					setValueCustom={setValue}
					placeholder="lawyer@email.com"
				/>
				<div className="flex items-center w-full justify-center gap-[15px] mt-12">
					<Button
						decoration="line-primary"
						size="full"
						label="Cancel"
						className="!h-16"
						classNameLabel="!font-semibold !text-base"
						onClick={() => {
							hideChangeEmail();
							reset({});
						}}
					/>
					<Button
						disabled={!isValid}
						decoration="primary"
						size="full"
						label="Change"
						className="!h-16"
						classNameLabel="!font-semibold !text-base"
						onClick={handleChangEmail}
					/>
				</div>
			</ModalChangeEmail>
		</>
	);
};
